/**
 * The one typed client for paio-commerce's diner routes (ORDR-42).
 *
 * Contracts, verified on paio-commerce main after #32 (COMM-132) and #33
 * (COMM-133):
 * - `POST carts/` with `fulfilment_context` opens a FOOD cart anonymously; the
 *   201 carries `cart_secret` ONCE.
 * - Every later cart call, the quote and checkout send
 *   `Authorization: Cart <cart_secret>`; without it the cart is a 404.
 * - `GET carts/<id>/quote/` prices the cart now and carries `quote_version`.
 * - `POST checkout/` for a FOOD cart REQUIRES `quote_version`: 400 without,
 *   409 `errors/commerce/stale-quote` when stale, and the cart stays open.
 * - Orders carry `status_cursor`, 0 at freeze and +1 per state change.
 * - COMM-182 (`65aeac8`): `payment_method` PAY_IN_STORE needs no token and is
 *   refused with a 403 `pay-in-store-not-enabled` where the brand has not
 *   enabled it; `contact`, `vehicle`, `delivery_address` and `kitchen_note`
 *   are validated against the cart's frozen mode, and a 400 names every bad
 *   field. The mode itself is the cart's, changed only by PATCHing its
 *   `fulfilment_context` while the cart is OPEN.
 *
 * THE SECRET NEVER LEAVES THIS MODULE'S HEADERS. It is not logged, not put in
 * a URL and not included in any error this module throws.
 */

import type { ApiClient, ApiResponse } from "../api/client.js";
import type { CartSecretStore } from "./cartSecretStore.js";
import type { WireSelection } from "../order/selection.js";
import type { FulfilmentContext } from "./flowFromWire.js";

export const STALE_QUOTE_TYPE = "https://api.paio.ai/errors/commerce/stale-quote";
export const PAY_IN_STORE_NOT_ENABLED_TYPE = "https://api.paio.ai/errors/commerce/pay-in-store-not-enabled";

export class CommerceRequestFailed extends Error {
  readonly status: number | null;
  readonly correlationId: string;

  constructor(operation: string, response: ApiResponse) {
    super(`${operation} failed with status ${response.status === null ? "network" : String(response.status)}`);
    this.name = "CommerceRequestFailed";
    this.status = response.status;
    this.correlationId = response.correlationId;
  }
}

export interface QuoteLine {
  readonly line_id: string;
  readonly offer_ref: string;
  readonly quantity: number;
  readonly unit_amount_minor: number;
  readonly line_total_minor: number;
}

/** One FOOD-chain rule's signed contribution to `total_minor` (COMM-133). */
export interface QuoteAdjustment {
  readonly rule_id: string;
  readonly rule_version: string;
  readonly amount_minor: number;
  /** `PROMOTION` or `REWARD` for a discount; `null` for anything that is not one. */
  readonly funding: string | null;
}

export interface Quote {
  readonly cart_id: string;
  readonly currency: string;
  readonly lines: readonly QuoteLine[];
  /**
   * The sum of line totals, and a line total is the BASE price only: options,
   * fees, tip and discounts are adjustments. Not a figure to show (ORDR-176).
   */
  readonly subtotal_minor: number;
  readonly tax_minor: number;
  readonly total_minor: number;
  readonly quote_version: string;
  /** Commerce always sends these; a fixture or an older quote may not. */
  readonly adjustments?: readonly QuoteAdjustment[];
}

export interface CommerceOrder {
  readonly order_id: string;
  readonly state: string;
  readonly status_cursor: number;
  readonly currency: string;
  readonly total_amount_minor: number;
  /** COMM-182: `CARD` or `PAY_IN_STORE`; an Order frozen before it reads `CARD`. */
  readonly payment_method?: string;
}

/**
 * Field errors as commerce named them: `contact.phone`, `vehicle`,
 * `delivery_address.postal_code` and so on, each with every reason given.
 */
export type FieldErrors = Readonly<Record<string, readonly string[]>>;

export type CheckoutOutcome =
  | { readonly kind: "placed"; readonly order: CommerceOrder }
  | { readonly kind: "stale-quote" }
  | { readonly kind: "pay-in-store-not-enabled" }
  | { readonly kind: "invalid"; readonly fieldErrors: FieldErrors; readonly correlationId: string };

/** The diner's handover answers, keyed exactly as `services/handover.py` reads them. */
export interface Handover {
  readonly contact: { readonly name: string; readonly phone: string };
  readonly vehicle?: { readonly description: string | null; readonly colour: string | null; readonly plate: string | null };
  readonly delivery_address?: {
    readonly line1: string;
    readonly line2: string | null;
    readonly city: string;
    readonly region: string | null;
    readonly postal_code: string;
  };
  readonly kitchen_note: string | null;
}

export type CheckoutPayment =
  | { readonly method: "PAY_IN_STORE" }
  | { readonly method: "CARD"; readonly sourceType: "CARD"; readonly token: string; readonly fraudSessionId: string };

export interface CheckoutInput {
  readonly cartId: string;
  readonly quoteVersion: string;
  readonly payment: CheckoutPayment;
  readonly handover: Handover;
}

/**
 * What a configured line adds to `offer_ref` and `quantity` (COMM-179,
 * ORDR-165): keys exactly as paio-commerce `api/carts.py`
 * `LINE_WRITABLE_FIELDS` names them. `portion_code` is `null` for an item sold
 * in one size. `selections` is the COMM-179 tree, `[]` when nothing was chosen.
 */
export interface LineConfiguration {
  readonly portion_code: string | null;
  readonly selections: readonly WireSelection[];
}

export interface CommerceClient {
  openCart(currency: string, context: FulfilmentContext): Promise<string>;
  /** Moves an OPEN cart to another context (the diner chose another mode). */
  setFulfilment(cartId: string, context: FulfilmentContext): Promise<void>;
  /** Without `configuration`, the line is `{offer_ref, quantity}` as before ORDR-165. */
  addLine(cartId: string, offerRef: string, quantity: number, configuration?: LineConfiguration): Promise<string>;
  removeLine(cartId: string, lineId: string): Promise<void>;
  quote(cartId: string): Promise<Quote>;
  checkout(input: CheckoutInput): Promise<CheckoutOutcome>;
  order(orderId: string): Promise<CommerceOrder>;
}

function dataOf(body: unknown): Record<string, unknown> | null {
  const data = (body as { data?: unknown } | null)?.data;
  return typeof data === "object" && data !== null ? (data as Record<string, unknown>) : null;
}

function isNum(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function parseQuote(body: unknown): Quote | null {
  const data = dataOf(body);
  if (
    data === null ||
    typeof data["cart_id"] !== "string" ||
    typeof data["currency"] !== "string" ||
    typeof data["quote_version"] !== "string" ||
    !isNum(data["subtotal_minor"]) ||
    !isNum(data["tax_minor"]) ||
    !isNum(data["total_minor"]) ||
    !Array.isArray(data["lines"])
  ) {
    return null;
  }
  return data as unknown as Quote;
}

function parseOrder(body: unknown): CommerceOrder | null {
  const data = dataOf(body);
  if (
    data === null ||
    typeof data["order_id"] !== "string" ||
    typeof data["state"] !== "string" ||
    !isNum(data["status_cursor"])
  ) {
    return null;
  }
  return data as unknown as CommerceOrder;
}

function fieldErrorsOf(body: unknown): FieldErrors {
  const record = body as Record<string, unknown> | null;
  const listed = record?.["errors"] ?? (record?.["error"] as Record<string, unknown> | undefined)?.["errors"];
  const errors: Record<string, string[]> = {};
  for (const entry of Array.isArray(listed) ? (listed as unknown[]) : []) {
    const field = (entry as Record<string, unknown> | null)?.["field"];
    const message = (entry as Record<string, unknown> | null)?.["message"];
    if (typeof field === "string" && typeof message === "string") {
      (errors[field] ??= []).push(message);
    }
  }
  return errors;
}

function paymentFields(payment: CheckoutPayment): Record<string, string> {
  // The token and fraud session ride only in this body, never a URL or header.
  return payment.method === "PAY_IN_STORE"
    ? { payment_method: payment.method }
    : {
        payment_method: payment.method,
        token: payment.token,
        source_type: payment.sourceType,
        fraud_session_id: payment.fraudSessionId,
      };
}

function problemType(body: unknown): string | null {
  const record = body as Record<string, unknown> | null;
  const direct = record?.["type"];
  const nested = (record?.["error"] as Record<string, unknown> | undefined)?.["type"];
  return typeof direct === "string" ? direct : typeof nested === "string" ? nested : null;
}

export function createCommerceClient(client: ApiClient, secrets: CartSecretStore): CommerceClient {
  function cartHeaders(cartId: string): Record<string, string> {
    const secret = secrets.read(cartId);
    return secret === null ? {} : { Authorization: `Cart ${secret}` };
  }

  return {
    async openCart(currency, context) {
      const response = await client.request("/commerce/carts/", {
        method: "POST",
        json: { currency, fulfilment_context: context },
      });
      const data = dataOf(response.body);
      const cartId = data?.["cart_id"];
      const secret = data?.["cart_secret"];
      if (response.status !== 201 || typeof cartId !== "string" || typeof secret !== "string") {
        throw new CommerceRequestFailed("open cart", response);
      }
      secrets.write(cartId, secret);
      return cartId;
    },

    async setFulfilment(cartId, context) {
      const response = await client.request(`/commerce/carts/${cartId}/`, {
        method: "PATCH",
        json: { fulfilment_context: context },
        headers: cartHeaders(cartId),
      });
      if (response.status !== 200) {
        throw new CommerceRequestFailed("change mode", response);
      }
    },

    async addLine(cartId, offerRef, quantity, configuration) {
      const response = await client.request(`/commerce/carts/${cartId}/lines/`, {
        method: "POST",
        json: { offer_ref: offerRef, quantity, ...configuration },
        headers: cartHeaders(cartId),
      });
      const lineId = dataOf(response.body)?.["line_id"];
      if (response.status !== 201 || typeof lineId !== "string") {
        throw new CommerceRequestFailed("add line", response);
      }
      return lineId;
    },

    async removeLine(cartId, lineId) {
      const response = await client.request(`/commerce/carts/${cartId}/lines/${lineId}/`, {
        method: "DELETE",
        headers: cartHeaders(cartId),
      });
      if (response.status !== 200 && response.status !== 204) {
        throw new CommerceRequestFailed("remove line", response);
      }
    },

    async quote(cartId) {
      const response = await client.request(`/commerce/carts/${cartId}/quote/`, {
        headers: cartHeaders(cartId),
      });
      const quote = response.status === 200 ? parseQuote(response.body) : null;
      if (quote === null) {
        throw new CommerceRequestFailed("quote", response);
      }
      return quote;
    },

    async checkout({ cartId, quoteVersion, payment, handover }) {
      // Field names exactly as paio-commerce api/checkout.py and
      // services/handover.py read them.
      const response = await client.request("/commerce/checkout/", {
        method: "POST",
        json: { cart_id: cartId, quote_version: quoteVersion, ...paymentFields(payment), ...handover },
        headers: cartHeaders(cartId),
      });
      const type = problemType(response.body);
      if (response.status === 403 && type === PAY_IN_STORE_NOT_ENABLED_TYPE) {
        return { kind: "pay-in-store-not-enabled" };
      }
      if (response.status === 400) {
        return { kind: "invalid", fieldErrors: fieldErrorsOf(response.body), correlationId: response.correlationId };
      }
      if (response.status === 409 && type === STALE_QUOTE_TYPE) {
        return { kind: "stale-quote" };
      }
      const order = response.status === 200 || response.status === 201 ? parseOrder(response.body) : null;
      if (order === null) {
        throw new CommerceRequestFailed("checkout", response);
      }
      // The secret has done its job: an order exists and the cart is closed.
      secrets.clear(cartId);
      return { kind: "placed", order };
    },

    async order(orderId) {
      const response = await client.request(`/commerce/orders/${orderId}/`);
      const order = response.status === 200 ? parseOrder(response.body) : null;
      if (order === null) {
        throw new CommerceRequestFailed("order status", response);
      }
      return order;
    },
  };
}
